/**
 * messagingEngine.ts
 *
 * Core messaging engine. Evaluates triggers, applies priority and suppression
 * rules, enforces caps, and returns the single resolved message for a given
 * placement context.
 *
 * Pure logic — no UI, no React. Called by useNutritionMessages and useHomeMessages.
 *
 * Target: src/services/messaging/messagingEngine.ts
 */

import { getTodayDate, getDateDaysAgo } from '../../utils/date';
import { getDailyNutrition, getTargetRanges } from '../nutrition.service';
import { getBodyMetrics, getTDEEConfig, getDailyTDEECached } from '../tdee.service';
import { getUserProfile } from '../userProfile.service';
import { getWorkoutEntriesForDateRange } from '../workout.service';
import { MESSAGE_DEFINITIONS } from './messagingMessages';
import * as messagingPersistence from './messagingPersistence';
import type { MessageId, ResolvedMessage, TriggerContext } from './messaging.types';

// ── Constants ─────────────────────────────────────────────────────────────────

/** T1/T2: only evaluate between 17:00 and 21:00 */
const T1_WINDOW_START = 17;
const T1_WINDOW_END = 21;

/** T1 protein trigger: below this % of target */
const T1_PROTEIN_THRESHOLD = 0.70;
/** T1 hydration trigger: below this % of minimum */
const T1_HYDRATION_THRESHOLD = 0.70;
/** T1 fibre trigger: below this % of target */
const T1_FIBRE_THRESHOLD = 0.70;

/** T3 weekly thresholds */
const T3_PROTEIN_THRESHOLD = 0.85;
const T3_HYDRATION_THRESHOLD = 0.85;
const T3_FIBRE_THRESHOLD = 0.85;
const T3_MIN_LOGGED_DAYS = 4;
const T3_UNDEREATING_MIN_DAYS = 3;
const T3_STRENGTH_LOOKBACK_DAYS = 30;

/** GLP-1 meal nudge 1 fires from 14:00, nudge 2 from 19:00 */
const GLP1_MEAL_1_HOUR = 14;
const GLP1_MEAL_2_HOUR = 19;
/** Qualifying meal threshold in kcal */
const MEAL_DEFINITION_KCAL = 200;

// ── Undereating threshold ──────────────────────────────────────────────────────

function calculateUndereatingThreshold(baseTDEE: number, sex: 'male' | 'female' | 'other'): number {
  const floor = sex === 'female' ? 1200 : 1500;
  return Math.max(floor, Math.round(baseTDEE * 0.7));
}

// ── Time window helpers ────────────────────────────────────────────────────────

function isInT1Window(now: Date): boolean {
  const h = now.getHours();
  return h >= T1_WINDOW_START && h < T1_WINDOW_END;
}

function isAfterHour(now: Date, hour: number): boolean {
  return now.getHours() >= hour;
}

function isMondayMorning(now: Date): boolean {
  return now.getDay() === 1; // 1 = Monday
}

// ── Context builder ────────────────────────────────────────────────────────────

async function buildTriggerContext(userId: string, now?: Date): Promise<TriggerContext | null> {
  const date = getTodayDate();
  const resolvedNow = now ?? new Date();

  const [nutrition, targets, bodyMetrics, tdeeConfig, profile] = await Promise.all([
    getDailyNutrition(userId, date).catch(() => null),
    getTargetRanges(userId).catch(() => null),
    getBodyMetrics(userId).catch(() => null),
    getTDEEConfig(userId).catch(() => null),
    getUserProfile(userId).catch(() => null),
  ]);

  const baseTDEE = tdeeConfig?.effectiveBaseTdee ?? tdeeConfig?.baseTdee ?? 2000;
  const sex = (bodyMetrics?.sex ?? 'male') as 'male' | 'female' | 'other';
  const glp1Enabled = profile?.glp1Enabled ?? false;

  const undereatingThreshold = calculateUndereatingThreshold(baseTDEE, sex);

  // Use safe defaults if targets failed to load — messaging still works,
  // rules that require targets will simply not fire (values stay at 0).
  return {
    userId,
    date,
    now: resolvedNow,
    nutrition: {
      totalCalories: nutrition?.totalCalories ?? 0,
      totalProtein: nutrition?.totalProtein ?? 0,
      totalHydration: nutrition?.totalHydration ?? 0,
      totalFibre: nutrition?.totalFibre ?? 0,
      entryCount: nutrition?.entries?.length ?? 0,
    },
    targets: {
      proteinMin: targets?.protein?.min ?? 0,
      hydrationMin: targets?.hydration?.min ?? 2000,
      fibreMin: targets?.fibre?.min ?? 25,
    },
    undereatingThreshold,
    profile: {
      glp1Enabled,
      sex,
    },
  };
}

// ── T1 trigger evaluation ──────────────────────────────────────────────────────

function evaluateT1(ctx: TriggerContext): MessageId | null {
  // Gate: must be in time window
  if (!isInT1Window(ctx.now)) return null;
  // Gate: must have logged food today (entry count guard)
  if (ctx.nutrition.entryCount === 0) return null;
  // Gate: belt-and-braces — suppress all nutrition messages when calories are zero.
  // Prevents protein/hydration/fibre messages firing on hydration-only state or
  // when stale cached data carries entries but today's food total is genuinely zero.
  if (ctx.nutrition.totalCalories === 0) return null;

  const { totalCalories, totalProtein, totalHydration, totalFibre } = ctx.nutrition;
  const { proteinMin, hydrationMin, fibreMin } = ctx.targets;

  // Priority 1: Undereating
  if (totalCalories < ctx.undereatingThreshold) return 'undereating';
  // Priority 2: Protein
  if (proteinMin > 0 && totalProtein < proteinMin * T1_PROTEIN_THRESHOLD) return 'protein_low';
  // Priority 3: Hydration
  if (hydrationMin > 0 && totalHydration < hydrationMin * T1_HYDRATION_THRESHOLD) return 'hydration_low';
  // Priority 4: Fibre
  if (fibreMin > 0 && totalFibre < fibreMin * T1_FIBRE_THRESHOLD) return 'fibre_low';

  return null;
}

// ── T2 trigger evaluation ──────────────────────────────────────────────────────

function evaluateT2(ctx: TriggerContext): MessageId | null {
  // Gate: must be in time window (same as T1)
  if (!isInT1Window(ctx.now)) return null;
  // Gate: must have logged food today (entry count guard)
  if (ctx.nutrition.entryCount === 0) return null;
  // Gate: belt-and-braces — suppress recovery messages when calories are zero
  if (ctx.nutrition.totalCalories === 0) return null;

  const { totalProtein, totalHydration } = ctx.nutrition;
  const { proteinMin, hydrationMin } = ctx.targets;

  // Protein recovered: crosses 100% of minimum, first time today
  if (
    proteinMin > 0 &&
    totalProtein >= proteinMin &&
    !messagingPersistence.hasRecoveredToday('protein_recovered')
  ) {
    return 'protein_recovered';
  }

  // Hydration recovered: crosses 100% of minimum, first time today
  if (
    hydrationMin > 0 &&
    totalHydration >= hydrationMin &&
    !messagingPersistence.hasRecoveredToday('hydration_recovered')
  ) {
    return 'hydration_recovered';
  }

  return null;
}

// ── T3 trigger evaluation ──────────────────────────────────────────────────────

async function evaluateT3(ctx: TriggerContext): Promise<MessageId | null> {
  // Gate: Monday morning only
  if (!isMondayMorning(ctx.now)) return null;

  // Fetch the prior 7 days (Mon–Sun, i.e. days 1–7 ago)
  const priorDates = Array.from({ length: 7 }, (_, i) => getDateDaysAgo(i + 1));

  // Fetch all nutrition data for prior week in parallel
  const nutritionResults = await Promise.all(
    priorDates.map(d => getDailyNutrition(ctx.userId, d).catch(() => null))
  );

  // Logged day = at least one saved nutrition entry
  const loggedDays = nutritionResults.filter(n => n && (n.entries?.length ?? 0) > 0);
  const loggedDayCount = loggedDays.length;

  // Calculate averages over logged days only
  let sumProtein = 0, sumHydration = 0, sumFibre = 0, sumCalories = 0;
  let daysBelowCalorieFloor = 0;

  for (const n of loggedDays) {
    if (!n) continue;
    sumProtein += n.totalProtein;
    sumHydration += n.totalHydration;
    sumFibre += n.totalFibre;
    sumCalories += n.totalCalories;
    if (n.totalCalories < ctx.undereatingThreshold) daysBelowCalorieFloor++;
  }

  const avgProtein = loggedDayCount > 0 ? sumProtein / loggedDayCount : 0;
  const avgHydration = loggedDayCount > 0 ? sumHydration / loggedDayCount : 0;
  const avgFibre = loggedDayCount > 0 ? sumFibre / loggedDayCount : 0;

  const { proteinMin, hydrationMin, fibreMin } = ctx.targets;

  // Priority 1: Under-eating pattern — ≥ 3 days below calorie floor
  if (daysBelowCalorieFloor >= T3_UNDEREATING_MIN_DAYS) {
    return 'weekly_undereating';
  }

  // Priority 2–4: Require ≥ 4 logged days
  if (loggedDayCount >= T3_MIN_LOGGED_DAYS) {
    // Priority 2: Protein
    if (proteinMin > 0 && avgProtein < proteinMin * T3_PROTEIN_THRESHOLD) {
      return 'weekly_protein_low';
    }
    // Priority 3: Hydration
    if (hydrationMin > 0 && avgHydration < hydrationMin * T3_HYDRATION_THRESHOLD) {
      return 'weekly_hydration_low';
    }
    // Priority 4: Fibre
    if (fibreMin > 0 && avgFibre < fibreMin * T3_FIBRE_THRESHOLD) {
      return 'weekly_fibre_low';
    }
  }

  // Priority 5: Strength missing
  // Only fires if user has logged ≥ 1 strength session in prior 30 days
  const priorWeekStart = priorDates[6]; // oldest of the 7 days
  const priorWeekEnd = priorDates[0];   // most recent of the 7 days

  const [priorWeekWorkouts, prior30Workouts] = await Promise.all([
    getWorkoutEntriesForDateRange(ctx.userId, priorWeekStart, priorWeekEnd).catch(() => []),
    getWorkoutEntriesForDateRange(
      ctx.userId,
      getDateDaysAgo(T3_STRENGTH_LOOKBACK_DAYS),
      priorDates[0],
    ).catch(() => []),
  ]);

  const hasStrengthThisWeek = priorWeekWorkouts.some(w => w.type === 'weightlifting');
  const hasStrengthLast30Days = prior30Workouts.some(w => w.type === 'weightlifting');

  if (!hasStrengthThisWeek && hasStrengthLast30Days) {
    return 'weekly_strength_missing';
  }

  // Priority 6: Logging inconsistent — fewer than 4 logged days
  if (loggedDayCount < T3_MIN_LOGGED_DAYS) {
    return 'weekly_logging_inconsistent';
  }

  return null;
}

// ── GLP-1 meal nudge evaluation ───────────────────────────────────────────────

async function evaluateGlp1Meal(ctx: TriggerContext): Promise<MessageId | null> {
  if (!ctx.profile.glp1Enabled) return null;
  if (ctx.nutrition.entryCount === 0) return null;

  // Fetch today's entries to count qualifying meals
  const nutrition = await getDailyNutrition(ctx.userId, ctx.date).catch(() => null);
  const qualifyingMeals = (nutrition?.entries ?? []).filter(
    e => (e.macros?.calories ?? 0) >= MEAL_DEFINITION_KCAL
  ).length;

  // Nudge 2 (19:00): fewer than 1 qualifying meal
  if (isAfterHour(ctx.now, GLP1_MEAL_2_HOUR) && qualifyingMeals < 1) {
    return 'glp1_meal_2';
  }

  // Nudge 1 (14:00): zero qualifying meals
  if (isAfterHour(ctx.now, GLP1_MEAL_1_HOUR) && qualifyingMeals === 0) {
    return 'glp1_meal_1';
  }

  return null;
}

// ── T6 recovery message variants ─────────────────────────────────────────────
// 13 variants from brief across 3 groups.
// Primary default + Group A (calm reassurance) + Group B (behavioural) + Group C (momentum, use sparingly).
// Rotated by day-of-week mod 13 so the same message is unlikely to repeat.
const RECOVERY_VARIANTS: Array<{ headline: string; body: string }> = [
  // Primary default
  { headline: 'Missed a day — just log your next meal', body: "You're still on track." },
  // Group A — calm reassurance
  { headline: 'Missed a day — that\'s normal', body: 'Just log your next meal.' },
  { headline: 'One quiet day happens', body: 'Pick up where you left off.' },
  { headline: 'No problem — consistency resumes with the next entry', body: 'Just log the next thing you eat.' },
  { headline: "You're still building the habit", body: 'Just log the next thing you eat.' },
  { headline: "A missed day doesn't reset progress", body: 'Start again now.' },
  // Group B — behavioural framing
  { headline: 'Consistency is built over time — not perfect days', body: 'Log your next meal to keep moving forward.' },
  { headline: 'Habits grow from returning, not from perfection', body: 'One entry keeps the routine alive.' },
  { headline: 'Progress continues when you restart quickly', body: 'Just log the next thing you eat.' },
  { headline: "You're still in the routine — just log the next meal", body: 'One entry is all it takes.' },
  // Group C — gentle momentum (use sparingly — positioned at end of rotation)
  { headline: "Let's get back into rhythm — log your next meal", body: 'One entry gets you back on track.' },
  { headline: 'Momentum returns with one simple entry', body: 'Log your next meal now.' },
  { headline: 'Restarting today keeps progress moving', body: 'Just log the next thing you eat.' },
];

function getRecoveryVariant(now: Date): { headline: string; body: string } {
  return RECOVERY_VARIANTS[now.getDay() % RECOVERY_VARIANTS.length];
}

// ── T6 trigger evaluation ──────────────────────────────────────────────────────
// Fires when:
//   - User has not logged today
//   - Yesterday had no entries (gap started)
//   - Prior history exists (not a new user)
//   - Has NOT already fired for this gap (once per gap — keyed by gap start date)
// Does NOT fire again on day 2+ of the same gap.
// Gap flag clears when user logs again (clearRecoveryGap called in engine on log detection).

async function evaluateT6Recovery(
  userId: string,
): Promise<{ shouldFire: boolean; gapStartDate: string } | null> {
  const today = getTodayDate();
  const yesterday = getDateDaysAgo(1);
  const twoDaysAgo = getDateDaysAgo(2);
  const threeDaysAgo = getDateDaysAgo(3);

  const [todayData, yesterdayData, twoDaysData, threeDaysData] = await Promise.all([
    getDailyNutrition(userId, today).catch(() => null),
    getDailyNutrition(userId, yesterday).catch(() => null),
    getDailyNutrition(userId, twoDaysAgo).catch(() => null),
    getDailyNutrition(userId, threeDaysAgo).catch(() => null),
  ]);

  const hasLoggedToday = (todayData?.entries?.length ?? 0) > 0;
  const hasLoggedYesterday = (yesterdayData?.entries?.length ?? 0) > 0;
  const hasLoggedTwoDaysAgo = (twoDaysData?.entries?.length ?? 0) > 0;
  const hasLoggedThreeDaysAgo = (threeDaysData?.entries?.length ?? 0) > 0;

  // If user logged today, clear any existing gap flag so future gaps can fire
  if (hasLoggedToday) {
    messagingPersistence.clearRecoveryGap();
    return null;
  }

  // Suppress if yesterday was logged — no gap yet
  if (hasLoggedYesterday) return null;

  // Suppress if no prior history — new user, not a gap
  if (!hasLoggedTwoDaysAgo && !hasLoggedThreeDaysAgo) return null;

  // Gap confirmed. Gap start = yesterday (first missed day).
  const gapStartDate = yesterday;

  // Once per gap — suppress if already fired for this gap
  if (messagingPersistence.hasRecoveryFiredForGap(gapStartDate)) return null;

  return { shouldFire: true, gapStartDate };
}

// ── T7 on-track signal evaluation ───────────────────────────────────────────
// All conditions must be met within the same Mon–Sun calendar week.
// Fires once ever — permanent one-time flag.
// Conditions:
//   - ≥5 logged days in the current calendar week (Mon–today)
//   - Average protein ≥ protein min target across logged days
//   - ≥2 exercise sessions in the current calendar week
//   - Average daily deficit between 200 and 1200 kcal (baseTdee − avgCalories)

async function evaluateT7OnTrack(ctx: TriggerContext): Promise<MessageId | null> {
  // Already fired — never fire again
  if (messagingPersistence.hasOnTrackEverShown()) return null;

  // Current Mon–Sun week: Mon = day-of-week offset back to Monday
  const now = ctx.now;
  const dayOfWeek = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const weekDates = Array.from({ length: dayOfWeek + 1 }, (_, i) => getDateDaysAgo(dayOfWeek - i));

  const startDate = weekDates[0];
  const endDate = weekDates[weekDates.length - 1];

  const [nutritionResults, workouts] = await Promise.all([
    Promise.all(weekDates.map(d => getDailyNutrition(ctx.userId, d).catch(() => null))),
    getWorkoutEntriesForDateRange(ctx.userId, startDate, endDate).catch(() => []),
  ]);

  const loggedDays = nutritionResults.filter(n => n && (n.entries?.length ?? 0) > 0);

  // Gate 1: ≥5 logged days this week
  if (loggedDays.length < 5) return null;

  // Gate 2: average protein ≥ min target
  const avgProtein = loggedDays.reduce((sum, n) => sum + (n?.totalProtein ?? 0), 0) / loggedDays.length;
  if (ctx.targets.proteinMin > 0 && avgProtein < ctx.targets.proteinMin) return null;

  // Gate 3: ≥2 exercise sessions this week
  const exerciseSessions = workouts.filter(w => (w.duration ?? 0) > 0).length;
  if (exerciseSessions < 2) return null;

  // Gate 4: average deficit 200–1200 kcal — real deficit including activity burn
  const deficitResults = await Promise.allSettled(
    weekDates.map(d => getDailyTDEECached(ctx.userId, d).catch(() => null))
  );
  const deficits = deficitResults
    .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled' && r.value?.deficit != null)
    .map(r => r.value.deficit as number);
  const avgDeficit = deficits.length > 0 ? deficits.reduce((s, d) => s + d, 0) / deficits.length : 0;
  if (deficits.length === 0 || avgDeficit < 200 || avgDeficit > 1200) return null;

  return 'on_track';
}

// ── T8 first progress signal variants ────────────────────────────────────────
// 15 variants from brief. Tone: directional, calm, identity-building.
// No numbers, no streaks, no celebration. Rotated by day-of-week.
const FIRST_PROGRESS_VARIANTS: Array<{ headline: string; body: string }> = [
  // Primary recommended (top 5)
  { headline: 'Consistency starting to build.', body: 'Keep logging to keep it moving.' },
  { headline: "You're forming a routine.", body: 'Every entry helps.' },
  { headline: 'Good start — keep logging.', body: 'This is how it begins.' },
  { headline: "You're getting into the habit.", body: 'Keep going.' },
  { headline: 'Momentum is beginning.', body: 'Stay consistent.' },
  // Slightly warmer variants
  { headline: "You're on your way.", body: 'Keep logging.' },
  { headline: 'This is how habits begin.', body: 'One entry at a time.' },
  { headline: 'A solid start.', body: 'Keep it going.' },
  { headline: "You're building consistency.", body: 'Keep logging.' },
  { headline: 'Routine is taking shape.', body: 'Stay with it.' },
  // Behaviour-focused variants
  { headline: 'Keep logging — you\'re doing it right.', body: 'Consistency is what makes this work.' },
  { headline: 'Logging regularly makes this work.', body: 'You\'re on the right track.' },
  { headline: "You're showing up — that's what matters.", body: 'Keep it going.' },
  { headline: 'Consistency comes from small steps.', body: 'You\'re taking them.' },
  { headline: "You're laying the foundation.", body: 'Keep logging.' },
];

function getFirstProgressVariant(): { headline: string; body: string } {
  return FIRST_PROGRESS_VARIANTS[Math.floor(Math.random() * FIRST_PROGRESS_VARIANTS.length)];
}

// ── T8 trigger evaluation ──────────────────────────────────────────────────────
// Fires when:
//   - User has logged at least 1 meal today
//   - Consecutive logged days is 1 or 2
//   - Has never fired before (one-time ever)
// Does not fire if user already has a longer streak — they're past the early phase.

async function evaluateT8FirstProgress(
  userId: string,
): Promise<boolean> {
  if (messagingPersistence.hasFirstProgressEverShown()) return false;

  const today = getTodayDate();
  const todayData = await getDailyNutrition(userId, today).catch(() => null);
  const hasLoggedToday = (todayData?.entries?.length ?? 0) > 0;
  if (!hasLoggedToday) return false;

  // Count consecutive logged days ending today
  let streak = 1;
  for (let i = 1; i <= 2; i++) {
    const d = await getDailyNutrition(userId, getDateDaysAgo(i)).catch(() => null);
    if ((d?.entries?.length ?? 0) > 0) streak++;
    else break;
  }

  // Only fire on day 1 or 2 — not if they already have a longer streak
  return streak <= 2;
}

// ── T9 normative reassurance variants ───────────────────────────────────────
// 20 variants across 4 groups from brief.
// Tone: calm, factual, anonymous, non-judgmental.
// Picked randomly at fire time — user sees one message ever within the window.
const NORMATIVE_VARIANTS: Array<{ headline: string; body: string }> = [
  // Primary recommended (top 5)
  { headline: 'This pattern is normal early on.', body: 'Most routines take a little time to settle.' },
  { headline: "You're progressing as expected.", body: 'Keep going.' },
  { headline: 'Early consistency takes a little time.', body: "You're on the right track." },
  { headline: 'Most people settle into a routine gradually.', body: 'Keep logging.' },
  { headline: "You're moving in the right direction.", body: 'Stay with it.' },
  // Slightly more informational
  { headline: 'Most routines take a few weeks to stabilise.', body: 'Keep going.' },
  { headline: 'Consistency usually builds step by step.', body: 'One entry at a time.' },
  { headline: 'Early progress often looks like this.', body: "You're doing fine." },
  { headline: "You're following a typical early pattern.", body: 'Keep logging.' },
  { headline: 'This stage is part of the process.', body: 'Stay consistent.' },
  // Reassurance for irregular logging
  { headline: 'Start–stop patterns are common at the beginning.', body: "You're still in it." },
  { headline: "It's normal for routines to feel uneven early on.", body: 'Keep coming back.' },
  { headline: 'Consistency builds gradually for most people.', body: 'Keep logging.' },
  { headline: 'Early routines often take time to settle.', body: "You're still progressing." },
  { headline: "You're still in the normal adjustment phase.", body: 'Keep going.' },
  // Short ultra-neutral
  { headline: 'This is normal early on.', body: 'Keep logging.' },
  { headline: "You're on track.", body: 'Stay consistent.' },
  { headline: "You're progressing normally.", body: 'Keep going.' },
  { headline: 'Routine takes time.', body: "You're building it." },
  { headline: "You're settling into the process.", body: 'Keep logging.' },
];

// ── T9 trigger evaluation ─────────────────────────────────────────────────────
// Fires when:
//   - User within first 21 days since first log
//   - At least 2 logged days total
//   - No active risk flags (not severely under-eating today)
//   - Not shown in last 3 days (timestamp cooldown)
// Hard stop: day 21. Max ~6–7 exposures total.

async function evaluateT9NormativeReassurance(
  userId: string,
  ctx: TriggerContext,
): Promise<boolean> {
  if (messagingPersistence.hasNormativeReassuranceEverShown()) return false;

  // Find first logged day to compute days since first log
  const last30 = Array.from({ length: 30 }, (_, i) => getDateDaysAgo(i));
  const results = await Promise.allSettled(last30.map(d => getDailyNutrition(userId, d).catch(() => null)));
  const loggedDates = last30.filter((_, i) => {
    const r = results[i];
    return r.status === 'fulfilled' && r.value && (r.value.entries?.length ?? 0) > 0;
  });

  // Need at least 2 logged days
  if (loggedDates.length < 2) return false;

  // Days since first log = index of oldest logged date in last30
  // last30[0] = today, last30[N] = N days ago — oldest = highest index
  const daysSinceFirstLog = last30.length - 1 - last30.lastIndexOf(loggedDates[loggedDates.length - 1]);
  if (daysSinceFirstLog > 21) return false;

  // Suppress if severe under-eating risk today
  if (ctx.nutrition.entryCount > 0 && ctx.nutrition.totalCalories > 0 &&
      ctx.nutrition.totalCalories < ctx.undereatingThreshold * 0.5) return false;

  return true;
}

// ── T10 pattern drop evaluator ──────────────────────────────────────────────
// Compares rolling 7-day vs previous 7-day windows for logging, protein, workouts.
// Priority: logging > protein > workout. Returns metric or null.
// Requires: user has >= 14 logged days (baseline established).
// Guardrails: only fires if previous 7 days show a real baseline.

interface PatternDropResult {
  metric: 'logging' | 'protein' | 'workout';
  messageId: 'pattern_drop_logging' | 'pattern_drop_protein' | 'pattern_drop_workout';
}

async function evaluateT10PatternDrop(
  userId: string,
  proteinMinTarget: number,
): Promise<PatternDropResult | null> {
  // Global 3-day cap
  if (messagingPersistence.hasAnyPatternDropShownInLast3Days()) return null;

  // Build date arrays: current 7 days (days 0-6 ago) and previous 7 days (days 7-13 ago)
  const current7 = Array.from({ length: 7 }, (_, i) => getDateDaysAgo(i));
  const prev7 = Array.from({ length: 7 }, (_, i) => getDateDaysAgo(i + 7));
  const all14 = [...current7, ...prev7];

  // Fetch all 14 days of nutrition in parallel
  const nutritionResults = await Promise.allSettled(
    all14.map(d => getDailyNutrition(userId, d).catch(() => null))
  );

  const getNutrition = (i: number) => {
    const r = nutritionResults[i];
    return r.status === 'fulfilled' ? r.value : null;
  };

  // Count logged days and protein-on-target days for each window
  const proteinThreshold = proteinMinTarget * 0.8;
  let currentLogged = 0, prevLogged = 0;
  let currentProtein = 0, prevProtein = 0;

  for (let i = 0; i < 7; i++) {
    const n = getNutrition(i);
    if ((n?.entries?.length ?? 0) > 0) {
      currentLogged++;
      if (proteinMinTarget > 0 && (n?.totalProtein ?? 0) >= proteinThreshold) currentProtein++;
    }
  }
  for (let i = 7; i < 14; i++) {
    const n = getNutrition(i);
    if ((n?.entries?.length ?? 0) > 0) {
      prevLogged++;
      if (proteinMinTarget > 0 && (n?.totalProtein ?? 0) >= proteinThreshold) prevProtein++;
    }
  }

  // Eligibility: user must have >= 14 days since first log.
  // We determine this by finding the earliest logged date across both windows.
  // If the oldest logged date is less than 14 days ago — too new, do not fire.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let oldestLoggedDate: Date | null = null;
  for (let i = 0; i < 14; i++) {
    const n = getNutrition(i);
    if ((n?.entries?.length ?? 0) > 0) {
      const d = new Date(all14[i] + 'T00:00:00');
      if (!oldestLoggedDate || d < oldestLoggedDate) oldestLoggedDate = d;
    }
  }
  if (!oldestLoggedDate) return null; // no logged days at all
  const daysSinceFirstLog = Math.floor((today.getTime() - oldestLoggedDate.getTime()) / (1000 * 60 * 60 * 24));
  if (daysSinceFirstLog < 14) return null; // too new

  // Check logging drop — priority 1
  if (!messagingPersistence.hasPatternDropShownInLast7Days('logging')) {
    const logDrop = prevLogged - currentLogged;
    if ((prevLogged >= 5 && currentLogged <= 2) || logDrop >= 3) {
      return { metric: 'logging', messageId: 'pattern_drop_logging' };
    }
  }

  // Check protein drop — priority 2
  if (!messagingPersistence.hasPatternDropShownInLast7Days('protein') && proteinMinTarget > 0) {
    const proteinDrop = prevProtein - currentProtein;
    if ((prevProtein >= 4 && currentProtein <= 1) || proteinDrop >= 3) {
      return { metric: 'protein', messageId: 'pattern_drop_protein' };
    }
  }

  // Check workout drop — priority 3
  if (!messagingPersistence.hasPatternDropShownInLast7Days('workout')) {
    try {
      const startDate14 = getDateDaysAgo(13);
      const endDate = getDateDaysAgo(0);
      const workouts = await getWorkoutEntriesForDateRange(userId, startDate14, endDate).catch(() => []);

      const currentResistance = workouts.filter(w =>
        w.type === 'weightlifting' && (w.duration ?? 0) > 0 &&
        current7.includes(w.date)
      ).length;
      const prevResistance = workouts.filter(w =>
        w.type === 'weightlifting' && (w.duration ?? 0) > 0 &&
        prev7.includes(w.date)
      ).length;
      // Fallback to all workout types if no resistance data
      const currentWorkouts = workouts.filter(w => current7.includes(w.date) && (w.duration ?? 0) > 0).length;
      const prevWorkouts = workouts.filter(w => prev7.includes(w.date) && (w.duration ?? 0) > 0).length;

      const resistanceDrop = prevResistance >= 2 && currentResistance === 0;
      const generalDrop = prevWorkouts >= 3 && currentWorkouts === 0;
      if (resistanceDrop || generalDrop) {
        return { metric: 'workout', messageId: 'pattern_drop_workout' };
      }
    } catch { /* non-critical */ }
  }

  return null;
}

// ── Resolve copy (apply GLP-1 variant if applicable) ─────────────────────────

function resolveMessage(id: MessageId, glp1Enabled: boolean): ResolvedMessage | null {
  const def = MESSAGE_DEFINITIONS.find(m => m.id === id);
  if (!def) return null;

  const useGlp1 = glp1Enabled && !!def.glp1Copy;
  const copy = useGlp1 ? def.glp1Copy! : def.copy;

  return {
    id: def.id,
    tier: def.tier,
    placement: def.placement,
    emoji: def.emoji,
    headline: copy.headline,
    body: copy.body,
    isGlp1Variant: useGlp1,
  };
}

// ── Public: evaluate for Nutrition screen ────────────────────────────────────

/**
 * Returns at most 1 message for the Nutrition screen (T1 or T2 only).
 * Returns null if no message qualifies, or notifications are off.
 *
 * Marks the message as shown in persistence when returning it.
 */
export async function evaluateNutritionMessage(
  userId: string,
  now?: Date,
): Promise<ResolvedMessage | null> {
  try {
    if (!userId) return null;

    const profile = await getUserProfile(userId).catch(() => null);
    if (profile?.notifications_enabled === false) return null;

    if (messagingPersistence.hasGeneralMessageShownToday()) return null;

    const ctx = await buildTriggerContext(userId, now);
    if (!ctx) return null;

    // Try T1 first
    const t1Id = evaluateT1(ctx);
    if (t1Id && !messagingPersistence.hasMessageShownToday(t1Id)) {
      messagingPersistence.markGeneralMessageShown(t1Id);
      return resolveMessage(t1Id, ctx.profile.glp1Enabled);
    }

    // T2 only if no T1 has fired today
    const t2Id = evaluateT2(ctx);
    if (t2Id) {
      messagingPersistence.markRecoveredToday(t2Id);
      messagingPersistence.markGeneralMessageShown(t2Id);
      return resolveMessage(t2Id, ctx.profile.glp1Enabled);
    }

    return null;
  } catch (error) {
    console.error('[evaluateNutritionMessage] Unexpected error:', error);
    return null;
  }
}

// ── Public: evaluate for Home screen ─────────────────────────────────────────

/**
 * Returns at most 1 message for the Home screen (T3, T4, or GLP-1 meal).
 * Returns null if no message qualifies.
 *
 * Priority:
 *   1. General T1 already fired today → suppress everything except A2HS check
 *   2. T3 weekly (Monday morning, week cap)
 *   3. T4 A2HS (caller passes hasSeenA2HS from Firestore)
 *   4. GLP-1 meal nudge (separate cap, suppressed if T1 fired today)
 *
 * Marks the message as shown in persistence when returning it.
 */
export async function evaluateHomeMessage(
  userId: string,
  opts: {
    hasSeenA2HS: boolean;
    hasEverLogged: boolean;
    platform: 'ios_safari' | 'ios_nonsafari' | 'android' | 'other';
  },
  now?: Date,
): Promise<ResolvedMessage | null> {
  try {
    if (!userId) return null;

    const profile = await getUserProfile(userId).catch(() => null);
    if (profile?.notifications_enabled === false) return null;

    const t1FiredToday = messagingPersistence.hasGeneralMessageShownToday();

    const resolvedNow = now ?? new Date();
    const ctx = await buildTriggerContext(userId, resolvedNow);

    if (!t1FiredToday && !messagingPersistence.hasWeeklyMessageShownThisWeek()) {
      if (ctx) {
        const t3Id = await evaluateT3(ctx);
        if (t3Id) {
          messagingPersistence.markWeeklyMessageShown(t3Id);
          return resolveMessage(t3Id, ctx.profile.glp1Enabled);
        }
      }
    }

    // T6 — Recovery nudge (once per gap, suppressed if T1/T3 fired today)
    if (!t1FiredToday && !messagingPersistence.hasGeneralMessageShownToday()) {
      const t6Result = await evaluateT6Recovery(userId);
      if (t6Result?.shouldFire) {
        const variant = getRecoveryVariant(resolvedNow);
        messagingPersistence.markRecoveryFiredForGap(t6Result.gapStartDate);
        messagingPersistence.markGeneralMessageShown('missed_day_recovery');
        return {
          id: 'missed_day_recovery',
          tier: 'T6_RECOVERY',
          placement: 'home',
          emoji: '🔄',
          headline: variant.headline,
          body: variant.body,
          isGlp1Variant: false,
        };
      }
    }

    // T4 — Add to Home Screen (one-time, Firestore flag passed in from hook)
    if (!opts.hasSeenA2HS && opts.hasEverLogged && opts.platform !== 'other') {
      const a2hsCopy = getA2HSCopy(opts.platform);
      return {
        id: 'add_to_home_screen',
        tier: 'T4',
        placement: 'home',
        emoji: '📲',
        headline: a2hsCopy.headline,
        body: a2hsCopy.body,
        isGlp1Variant: false,
      };
    }

    // T7 — On-track signal (one-time ever, all conditions in same week)
    if (!messagingPersistence.hasOnTrackEverShown()) {
      if (ctx) {
        const t7Id = await evaluateT7OnTrack(ctx);
        if (t7Id) {
          messagingPersistence.markOnTrackShown();
          return resolveMessage(t7Id, ctx.profile.glp1Enabled);
        }
      }
    }

    // T11 — A1 first-entry preview (one-time ever, high priority, below T1 safety)
    // Fires when: today has at least 1 nutrition entry OR 1 workout entry, and never shown before.
    // Nutrition entry wins over workout if both exist (nutrition logged first in most cases).
    if (!messagingPersistence.hasA1FirstEntryShown()) {
      const todayNutritionEntries = ctx?.nutrition?.entryCount ?? 0;
      const hasNutritionEntry = todayNutritionEntries > 0;
      // For workout detection: fetch today's workouts
      const todayDate = getTodayDate();
      let hasWorkoutEntry = false;
      try {
        const todayWorkouts = await getWorkoutEntriesForDateRange(userId, todayDate, todayDate).catch(() => []);
        hasWorkoutEntry = todayWorkouts.length > 0;
      } catch { /* non-critical */ }

      if (hasNutritionEntry || hasWorkoutEntry) {
        const type = hasNutritionEntry ? 'nutrition' : 'workout';
        const id = hasNutritionEntry ? 'first_entry_nutrition' : 'first_entry_workout';
        messagingPersistence.markA1FirstEntryShown(type);
        messagingPersistence.markGeneralMessageShown(id);
        return {
          id,
          tier: 'T11_FIRST_ENTRY',
          placement: 'home',
          emoji: '📊',
          headline: type === 'nutrition'
            ? 'Good start — your daily picture is beginning to build.'
            : "Great start — your activity is now part of today's picture.",
          body: 'Keep logging to get the most from CoreTrak.',
          isGlp1Variant: false,
        };
      }
    }

    // T8 — First progress signal (one-time ever, 1–2 consecutive logged days)
    if (!messagingPersistence.hasFirstProgressEverShown()) {
      const shouldFire = await evaluateT8FirstProgress(userId);
      if (shouldFire) {
        const variant = getFirstProgressVariant();
        messagingPersistence.markFirstProgressShown();
        return {
          id: 'first_progress_signal',
          tier: 'T8_FIRST_PROGRESS',
          placement: 'home',
          emoji: '🌱',
          headline: variant.headline,
          body: variant.body,
          isGlp1Variant: false,
        };
      }
    }

    // T10 — Pattern-detection guidance (7-day per metric, 3-day global, below T8, above T9)
    if (!t1FiredToday && !messagingPersistence.hasGeneralMessageShownToday() && ctx) {
      const proteinMin = ctx.targets.proteinMin ?? 0;
      const dropResult = await evaluateT10PatternDrop(userId, proteinMin);
      if (dropResult) {
        messagingPersistence.markPatternDropShown(dropResult.metric);
        messagingPersistence.markGeneralMessageShown(dropResult.messageId);
        return resolveMessage(dropResult.messageId, ctx.profile.glp1Enabled);
      }
    }

    // T9 — Normative reassurance (3-day cooldown, suppress after day 21, low priority)
    if (!t1FiredToday && !messagingPersistence.hasGeneralMessageShownToday()) {
      if (ctx) {
        const shouldFire = await evaluateT9NormativeReassurance(userId, ctx);
        if (shouldFire) {
          const variant = NORMATIVE_VARIANTS[Math.floor(Math.random() * NORMATIVE_VARIANTS.length)];
          messagingPersistence.markNormativeReassuranceShown();
          messagingPersistence.markGeneralMessageShown('normative_reassurance');
          return {
            id: 'normative_reassurance',
            tier: 'T9_NORMATIVE',
            placement: 'home',
            emoji: '💬',
            headline: variant.headline,
            body: variant.body,
            isGlp1Variant: false,
          };
        }
      }
    }

    // GLP-1 meal nudge — separate cap, suppressed if T1 fired today
    if (!t1FiredToday && !messagingPersistence.hasGlp1MealShownToday()) {
      if (ctx) {
        const glp1Id = await evaluateGlp1Meal(ctx);
        if (glp1Id) {
          messagingPersistence.markGlp1MealShown(glp1Id);
          return resolveMessage(glp1Id, false);
        }
      }
    }

    return null;
  } catch (error) {
    console.error('[evaluateHomeMessage] Unexpected error:', error);
    return null;
  }
}

// ── A2HS copy ─────────────────────────────────────────────────────────────────

function getA2HSCopy(platform: 'ios_safari' | 'ios_nonsafari' | 'android' | 'other'): { headline: string; body: string } {
  switch (platform) {
    case 'ios_safari':
      return {
        headline: 'Add CoreTrak to your Home Screen',
        body: 'Tap Share → Add to Home Screen',
      };
    case 'android':
      return {
        headline: 'Add CoreTrak to your Home Screen',
        body: 'Tap the menu → Add to Home Screen',
      };
    case 'ios_nonsafari':
      return {
        headline: 'Open CoreTrak in Safari to add it to your Home Screen',
        body: 'Add to Home Screen works in Safari on iPhone.',
      };
    default:
      return {
        headline: 'Add CoreTrak to your Home Screen',
        body: 'For the best experience, add it to your Home Screen.',
      };
  }
}
